import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { StarIcon } from '@heroicons/react/24/solid';
import { useAddReviewMutation, useUpdateReviewMutation, useGetExistingCustomerReviewQuery, useGetExistingOrderCustomerReviewQuery } from '../../../redux/apis/endpoints/reviewApi';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import toast from 'react-hot-toast';
import { useGetCustomerProfileForReviewQuery } from '../../../redux/apis/endpoints/profileApi';
import { format } from 'date-fns';
import CustomerProfilePicture from '../../../components/ui/CustomerProfilePicture';
import Button from '../../../components/ui/Button';
import FormInput from '../../../components/ui/FormInput';
import { UserCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

const userLang = navigator.language || navigator.userLanguage;

const CustomerReviewPage = () => {
  const { customerId, fulfillmentGroupId: fulfillmentGroupIdFromParams, orderId: orderIdFromParams } = useParams();
  const history = useHistory();
  const location = useLocation();
  const user = useSelector(state => state.auth.user);
  
  // State from location (passed from navigation)
  const authorMerchantId = location.state?.authorMerchantId || user?.id;
  const existingReviewFromState = location.state?.existingReview;
  const orderNumber = location.state?.orderNumber;
  
  // Determine if this is an order-level or fulfillment group-level review
  const isOrderLevelReview = !!orderIdFromParams;
  const orderId = orderIdFromParams || location.state?.orderId;
  const fulfillmentGroupId = fulfillmentGroupIdFromParams;

  // Get customer profile for the review
  const { data: customer, isLoading: isLoadingCustomer } = 
    useGetCustomerProfileForReviewQuery(customerId, { skip: !customerId });

  // Get existing review based on context (order-level or FG-level)
  // For FG-level reviews, make the API call
  const { 
    data: existingFGReview, 
    isLoading: isLoadingFGReview 
  } = useGetExistingCustomerReviewQuery(
    { 
      fulfillmentGroupId,
      reviewedCustomerId: customerId,
      authorMerchantId 
    },
    { 
      skip: isOrderLevelReview || !fulfillmentGroupId || !customerId || !authorMerchantId 
    }
  );

  // For order-level reviews, check for existing order-level customer review
  const { 
    data: existingOrderReview, 
    isLoading: isLoadingOrderReview 
  } = useGetExistingOrderCustomerReviewQuery(
    { 
      orderId,
      authorMerchantId 
    },
    { 
      skip: !isOrderLevelReview || !orderId || !authorMerchantId 
    }
  );

  // Debug logging for API call
  useEffect(() => {
    if (isOrderLevelReview && orderId && authorMerchantId) {
      console.log('CustomerReviewPage - API call parameters:', {
        orderId,
        authorMerchantId,
        customerId,
        isOrderLevelReview,
        existingOrderReview,
        isLoadingOrderReview
      });
    }
  }, [isOrderLevelReview, orderId, authorMerchantId, customerId, existingOrderReview, isLoadingOrderReview]);

  // Determine which existing review to use
  let existingReview;
  let isLoadingExistingReview;

  if (isOrderLevelReview) {
    // For order-level reviews, prioritize the API response, then fall back to state
    existingReview = existingOrderReview || existingReviewFromState;
    isLoadingExistingReview = isLoadingOrderReview;
  } else {
    // For FG-level reviews, use the FG review
    existingReview = existingFGReview;
    isLoadingExistingReview = isLoadingFGReview;
  }

  // Auto-redirect logic for backward compatibility
  useEffect(() => {
    if (!fulfillmentGroupId && !orderId) {
      console.warn('CustomerReviewPage: Neither fulfillmentGroupId nor orderId found in URL params. Redirecting to dashboard.');
      history.push('/dashboard');
    }
  }, [customerId, fulfillmentGroupId, orderId, history, location.state]);

  // Set up review mutations
  const [addReview, { isLoading: isAddingReview }] = useAddReviewMutation();
  const [updateReview, { isLoading: isUpdatingReview }] = useUpdateReviewMutation();

  // Form state
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');

  // Helper to handle both single object and array responses
  const getReviewData = (review) => {
    if (!review) return null;
    return Array.isArray(review) ? review[0] : review;
  };

  // Load existing review data
  const reviewData = getReviewData(existingReview);
  const editingReviewId = reviewData?.id;
  
  useEffect(() => {
    if (reviewData) {
      console.log(`Editing existing ${isOrderLevelReview ? 'order' : 'fulfillment group'}-level customer review:`, reviewData);
      setRating(reviewData.rating || 5);
      // Both order-level and FG-level reviews use the same comment model with 'comment' field
      setComment(reviewData.comment || '');
      document.title = `Edit Customer Review`;
    } else {
      document.title = `Write Customer Review`;
    }
  }, [reviewData, isOrderLevelReview]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!comment.trim()) {
      toast.error(userLang === 'zh-CN' ? '请填写评价内容' : 'Please fill in the review content');
      return;
    }

    try {
      const submitData = {
        customers: [parseInt(customerId, 10)],
        // Both order-level and FG-level reviews use the same comment model with 'comment' field
        comment: comment.trim(),
        rating: parseInt(rating, 10),
        isReviewCustomer: true,
        isReviewProduct: false,
        // Include appropriate relationship field
        orders: isOrderLevelReview && orderId ? [parseInt(orderId, 10)] : [],
        fulfillmentGroup: !isOrderLevelReview && fulfillmentGroupId ? parseInt(fulfillmentGroupId, 10) : null,
      };

      if (editingReviewId) {
        console.log(`Updating ${isOrderLevelReview ? 'order' : 'fulfillment group'}-level customer review with data:`, submitData);
        await updateReview({ id: editingReviewId, ...submitData }).unwrap();
        toast.success(`Customer review updated successfully!`);
      } else {
        console.log(`Submitting ${isOrderLevelReview ? 'order' : 'fulfillment group'}-level customer review with data:`, submitData);
        await addReview(submitData).unwrap();
        toast.success(`Customer review submitted successfully!`);
      }

      // Navigate back
      history.goBack();
    } catch (err) {
      console.error(`Failed to submit ${isOrderLevelReview ? 'order' : 'fulfillment group'}-level customer review:`, err);
      const errorMessage = err.data?.error?.message || err.data?.message || 'Failed to submit review';
      toast.error(errorMessage);
    }
  };

  // Loading states
  if (isLoadingCustomer || isLoadingExistingReview) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  const isLoading = isAddingReview || isUpdatingReview;
  const pageTitle = editingReviewId ? 'Edit Customer Review' : 'Write a Review';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Header Section with Teal Gradient */}
          <div className="bg-gradient-to-r from-brand-teal to-brand-teal-light px-6 py-8 text-white">
            <h1 className="text-3xl font-bold mb-2">
              {pageTitle}
            </h1>
            {orderNumber && (
              <p className="text-sm text-white">
                {isOrderLevelReview ? 'Order' : 'Fulfillment Group'}: {orderNumber}
              </p>
            )}
          </div>

          <div className="p-6">
            {/* Customer Information Card */}
            {customer ? (
              <div className="mb-8 p-6 flex justify-center">
                <div className="flex flex-col items-center space-y-4">
                  <CustomerProfilePicture 
                    customer={customer} 
                    size="xl"
                    showTooltip={false}
                  />
                  <div className="text-center">
                    <h4 className="text-lg font-medium text-gray-900">
                      {customer.username || customer.firstname || 'Customer'}
                    </h4>
                    {customer.firstname && customer.lastname && (
                      <p className="text-sm text-gray-600">
                        {customer.firstname} {customer.lastname}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-8 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800 text-center">Customer information not available</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Rating */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Rating
                </label>
                <div className="flex items-center space-x-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      className={`p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-brand-teal focus:ring-offset-2 transition-colors ${
                        star <= rating ? 'text-yellow-400 hover:text-yellow-500' : 'text-gray-300 hover:text-gray-400'
                      }`}
                      disabled={isLoading}
                    >
                      <StarIcon className="h-8 w-8" />
                    </button>
                  ))}
                  <span className="ml-3 text-sm text-gray-600">
                    {rating > 0 ? `${rating} out of 5 stars` : 'Select a rating'}
                  </span>
                </div>
              </div>

              {/* Review Comment */}
              <div className="mb-6">
                <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-3">
                  Review
                </label>
                <textarea
                  id="comment"
                  rows={6}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal resize-none"
                  placeholder="Share your experience with this purchase..."
                />
                <p className="mt-2 text-sm text-gray-500">
                  Tell other customers about your experience with this product and seller.
                </p>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => history.goBack()}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-brand-teal text-white rounded-lg shadow-sm hover:bg-brand-teal-dark focus:outline-none focus:ring-2 focus:ring-brand-teal focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors font-medium"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      {reviewData ? 'Updating...' : 'Submitting...'}
                    </>
                  ) : (
                    reviewData ? 'Update Review' : 'Submit Review'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerReviewPage; 